import { API_URL } from '../../config';
import { getSkillById } from '../gphSkillRegistry';
import { extractPubgCalculatorSlots } from '../slotFilling/pubgCalculatorSlotExtractor';
import {
  validatePubgCalculatorSlots,
  buildMissingPubgSlotsMessage,
  buildInvalidPubgSlotsMessage
} from '../validation/pubgCalculatorSlotValidator';
import {
  renderPubgCalculatorResult,
  renderPubgCalculatorHumanError
} from '../renderers/pubgCalculatorResultRenderer';

const mapSensitivityTypeToControlMode = (sensitivityType) => (
  ['Gyro', 'ADS Gyro'].includes(sensitivityType) ? 'gyro' : 'camera'
);

export const createPubgCalculatorRequestId = () => (
  `pubg_skill_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
);

export const buildPubgCalculatorPayload = (slots, {
  requestId = createPubgCalculatorRequestId(),
  language = 'pt'
} = {}) => ({
  weapon: slots.weapon,
  mainScope: slots.baseScope,
  knownPerfectValue: slots.baseValue,
  controlMode: mapSensitivityTypeToControlMode(slots.sensitivityType),
  requestId,
  language
});

const readResponseJson = async (response) => {
  try {
    return await response.json();
  } catch (error) {
    return {};
  }
};

const extractBackendMessage = (data = {}) => {
  const detail = data.detail || data.message || data.error;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail.message === 'string') return detail.message;
  return null;
};

export const preparePubgMobileCalculatorSkill = ({ text = '', slots = {} } = {}) => {
  const skill = getSkillById('pubg_mobile_calculator');
  const extraction = extractPubgCalculatorSlots(text, slots);

  if (extraction.unsupported) {
    return {
      status: 'unsupported',
      skill,
      slots: extraction.slots,
      missingSlots: [],
      invalidSlots: [{
        slot: 'game',
        value: 'codm',
        reason: 'codm_calculator_unavailable'
      }],
      message: extraction.unsupported.message
    };
  }

  const validation = validatePubgCalculatorSlots(extraction.slots, extraction.invalidSlots);

  if (validation.invalidSlots.length > 0) {
    return {
      status: 'invalid',
      skill,
      slots: validation.normalizedSlots,
      missingSlots: validation.missingSlots,
      invalidSlots: validation.invalidSlots,
      message: buildInvalidPubgSlotsMessage(validation.invalidSlots)
    };
  }

  if (validation.missingSlots.length > 0) {
    return {
      status: 'needs_slots',
      skill,
      slots: validation.normalizedSlots,
      missingSlots: validation.missingSlots,
      invalidSlots: [],
      message: buildMissingPubgSlotsMessage(validation.missingSlots, validation.normalizedSlots)
    };
  }

  const payload = buildPubgCalculatorPayload(validation.normalizedSlots);

  return {
    status: 'ready',
    skill,
    slots: validation.normalizedSlots,
    missingSlots: [],
    invalidSlots: [],
    payload,
    message: null
  };
};

export const executePubgMobileCalculatorSkill = async ({
  text = '',
  slots = {},
  fetchImpl = globalThis.fetch,
  apiBaseUrl = API_URL,
  authToken = null,
  getAuthToken = null,
  requestId = null,
  language = 'pt'
} = {}) => {
  const prepared = preparePubgMobileCalculatorSkill({ text, slots });

  if (prepared.status !== 'ready') {
    return prepared;
  }

  if (typeof fetchImpl !== 'function') {
    return {
      ...prepared,
      status: 'error',
      message: 'Nao consegui conectar a calculadora agora. Tenta novamente em instantes.'
    };
  }

  const payload = buildPubgCalculatorPayload(prepared.slots, {
    requestId: requestId || prepared.payload.requestId,
    language
  });

  let response;
  try {
    const token = authToken || (typeof getAuthToken === 'function' ? await getAuthToken() : null);
    response = await fetchImpl(`${apiBaseUrl}/pubg-calculator/analyze`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify(payload)
    });
  } catch (error) {
    return {
      ...prepared,
      status: 'error',
      payload,
      message: 'Nao consegui conectar a calculadora agora. Tenta novamente em instantes.'
    };
  }

  const data = await readResponseJson(response);

  if (!response.ok) {
    return {
      ...prepared,
      status: 'blocked_or_error',
      payload,
      backendStatus: response.status,
      message: renderPubgCalculatorHumanError({
        status: response.status,
        message: extractBackendMessage(data)
      })
    };
  }

  return {
    ...prepared,
    status: 'success',
    payload,
    result: renderPubgCalculatorResult(data, {
      slots: prepared.slots,
      payload
    })
  };
};

export default executePubgMobileCalculatorSkill;
